/**
 * DashboardPage — Phase 10 full dashboard.
 *
 * Sections:
 *  1. Greeting + date
 *  2. Overall stat bar (total completed / published / in_progress / not_started)
 *  3. Per-audience progress cards with SVG rings
 *  4. Needs Attention — in_progress days across all visible audiences
 *  5. Recent Activity — last 12 audit log entries
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  BookOpen,
  AlertCircle,
  Activity,
  RefreshCw,
  FileText,
  Music,
  Image,
  LogIn,
  LogOut,
  Users,
  Shield,
  Copy,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAuth } from '@/hooks/useAuth';
import { useAudienceStatuses } from '@/hooks/useContent';
import { useRecentActivity }   from '@/hooks/useDashboard';
import { getDayInfo } from '@/config/pregnancyCalendar';
import { AUDIENCES, AUDIENCE_LABELS } from '@/types/auth.types';
import type { Audience } from '@/types/auth.types';
import type { ContentStatus, AuditLog } from '@/types';
import { AUDIT_ACTION_LABELS } from '@/types/audit.types';

// ─── Helpers ───────────────────────────────────────────────────────────────────

const TOTAL_DAYS = 280;

const AUDIENCE_RING_COLOR: Record<Audience, string> = {
  mothers:      '#ec4899', // pink-500
  fathers:      '#3b82f6', // blue-500
  grandparents: '#a855f7', // purple-500
};

const AUDIENCE_BG: Record<Audience, string> = {
  mothers:      'bg-pink-50   border-pink-200',
  fathers:      'bg-blue-50   border-blue-200',
  grandparents: 'bg-purple-50 border-purple-200',
};

const AUDIENCE_DOT: Record<Audience, string> = {
  mothers:      'bg-pink-500',
  fathers:      'bg-blue-500',
  grandparents: 'bg-purple-500',
};

const AUDIENCE_TEXT: Record<Audience, string> = {
  mothers:      'text-pink-600',
  fathers:      'text-blue-600',
  grandparents: 'text-purple-600',
};

interface AudienceStats {
  published:     number;
  completed:     number;
  in_progress:   number;
  not_started:   number;
  inProgressDays:number[];
  doneCount:     number;   // published + completed
  percentage:    number;   // doneCount / 280 * 100
}

function computeStats(
  statusMap: Map<number, ContentStatus> | undefined,
): AudienceStats {
  const s: AudienceStats = {
    published:     0,
    completed:     0,
    in_progress:   0,
    not_started:   0,
    inProgressDays:[],
    doneCount:     0,
    percentage:    0,
  };
  if (!statusMap) return s;

  for (const [dayNumber, status] of statusMap) {
    if      (status === 'published')   s.published++;
    else if (status === 'completed')   s.completed++;
    else if (status === 'in_progress') { s.in_progress++; s.inProgressDays.push(dayNumber); }
    else                               s.not_started++;
  }
  // Days missing from map are not_started
  s.not_started += TOTAL_DAYS - statusMap.size;
  s.doneCount    = s.published + s.completed;
  s.percentage   = Math.round((s.doneCount / TOTAL_DAYS) * 100);
  return s;
}

function formatRelativeTime(date: Date): string {
  const diff    = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1)  return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)   return `${hours}h ago`;
  const days  = Math.floor(hours / 24);
  if (days < 7)     return `${days}d ago`;
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-AU', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

// ─── SVG Progress Ring ─────────────────────────────────────────────────────────

interface ProgressRingProps {
  percentage: number;
  color:      string;
  size?:      number;
  stroke?:    number;
}

function ProgressRing({ percentage, color, size = 100, stroke = 9 }: ProgressRingProps) {
  const r     = (size - stroke) / 2;
  const circ  = 2 * Math.PI * r;
  const offset = circ - (percentage / 100) * circ;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
      {/* Track */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="#e2e8f0"
        strokeWidth={stroke}
      />
      {/* Progress */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
      />
    </svg>
  );
}

// ─── Audit action icon ─────────────────────────────────────────────────────────

function ActionIcon({ action }: { action: string }) {
  const cls = 'h-3.5 w-3.5';
  if (action.startsWith('content_copy')) return <Copy  className={cls} />;
  if (action.startsWith('content'))      return <FileText className={cls} />;
  if (action.startsWith('audio'))        return <Music    className={cls} />;
  if (action.startsWith('image'))        return <Image    className={cls} />;
  if (action === 'login')                return <LogIn    className={cls} />;
  if (action === 'logout')               return <LogOut   className={cls} />;
  if (action.startsWith('user'))         return <Users    className={cls} />;
  if (action.startsWith('permission') || action.startsWith('role')) return <Shield className={cls} />;
  return <Activity className={cls} />;
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const { cmsUser, isAdmin } = useAuth();

  // Load status maps for all 3 audiences
  const mothersQ      = useAudienceStatuses('mothers');
  const fathersQ      = useAudienceStatuses('fathers');
  const grandparentsQ = useAudienceStatuses('grandparents');

  const { data: recentActivity, isLoading: activityLoading } = useRecentActivity(12);

  // Compute per-audience stats
  const stats = useMemo(() => ({
    mothers:      computeStats(mothersQ.data),
    fathers:      computeStats(fathersQ.data),
    grandparents: computeStats(grandparentsQ.data),
  }), [mothersQ.data, fathersQ.data, grandparentsQ.data]);

  // Overall totals across all 3 audiences (only visible ones contribute).
  // Admins always see all audiences regardless of their permission settings.
  const visibleAudiences = AUDIENCES.filter(
    a => isAdmin || cmsUser?.permissions[a]?.view !== false,
  );

  const overall = useMemo(() => {
    let published = 0, completed = 0, in_progress = 0, not_started = 0;
    for (const a of visibleAudiences) {
      published   += stats[a].published;
      completed   += stats[a].completed;
      in_progress += stats[a].in_progress;
      not_started += stats[a].not_started;
    }
    const total    = visibleAudiences.length * TOTAL_DAYS;
    const done     = published + completed;
    const pct      = total > 0 ? Math.round((done / total) * 100) : 0;
    return { published, completed, in_progress, not_started, total, done, pct };
  }, [stats, visibleAudiences]);

  // Needs attention: in_progress days across all visible audiences (max 15)
  const needsAttention = useMemo(() => {
    const items: { audience: Audience; dayNumber: number }[] = [];
    for (const a of visibleAudiences) {
      for (const d of stats[a].inProgressDays) {
        items.push({ audience: a, dayNumber: d });
      }
    }
    items.sort((a, b) => a.dayNumber - b.dayNumber);
    return items.slice(0, 15);
  }, [stats, visibleAudiences]);

  const isLoadingStats = mothersQ.isLoading || fathersQ.isLoading || grandparentsQ.isLoading;

  return (
    <div className="space-y-6 max-w-6xl">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">
            Welcome back, {cmsUser?.displayName?.split(' ')[0] ?? 'there'} 👋
          </h1>
          <p className="text-sm text-surface-500 mt-0.5">
            {formatDate(new Date())}
          </p>
        </div>

        {isLoadingStats && (
          <div className="flex items-center gap-2 text-xs text-surface-400">
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            Loading stats…
          </div>
        )}
      </div>

      {/* ── Overall stat bar ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          {
            label: 'Published',
            value: overall.published,
            icon:  <CheckCircle2 className="h-5 w-5 text-indigo-500" />,
            bg:    'bg-indigo-50',
            text:  'text-indigo-700',
          },
          {
            label: 'Completed',
            value: overall.completed,
            icon:  <BookOpen className="h-5 w-5 text-emerald-500" />,
            bg:    'bg-emerald-50',
            text:  'text-emerald-700',
          },
          {
            label: 'In Progress',
            value: overall.in_progress,
            icon:  <Clock className="h-5 w-5 text-amber-500" />,
            bg:    'bg-amber-50',
            text:  'text-amber-700',
          },
          {
            label: 'Not Started',
            value: overall.not_started,
            icon:  <AlertCircle className="h-5 w-5 text-slate-400" />,
            bg:    'bg-slate-50',
            text:  'text-slate-600',
          },
        ].map(card => (
          <div
            key={card.label}
            className="rounded-xl border border-surface-200 bg-white p-4 shadow-card"
          >
            <div className={cn('inline-flex rounded-lg p-2 mb-3', card.bg)}>
              {card.icon}
            </div>
            <p className={cn('text-2xl font-bold', card.text)}>
              {isLoadingStats ? '—' : card.value.toLocaleString()}
            </p>
            <p className="text-xs text-surface-500 mt-0.5">{card.label}</p>
          </div>
        ))}
      </div>

      {/* ── Per-audience progress ─────────────────────────────────────────── */}
      <section>
        <h2 className="text-sm font-semibold text-surface-500 uppercase tracking-wide mb-3">
          Progress by Audience
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {AUDIENCES.filter(a => cmsUser?.permissions[a]?.view !== false).map(audience => {
            const s   = stats[audience];
            const q   = audience === 'mothers' ? mothersQ
                       : audience === 'fathers' ? fathersQ
                       : grandparentsQ;

            return (
              <div
                key={audience}
                className="rounded-xl border border-surface-200 bg-white p-5 shadow-card flex flex-col"
              >
                {/* Audience label */}
                <div className="flex items-center gap-2 mb-4">
                  <span className={cn('h-2.5 w-2.5 rounded-full shrink-0', AUDIENCE_DOT[audience])} />
                  <span className={cn('text-sm font-semibold', AUDIENCE_TEXT[audience])}>
                    {AUDIENCE_LABELS[audience]}
                  </span>
                </div>

                {/* Ring + completion */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="relative shrink-0">
                    <ProgressRing
                      percentage={q.isLoading ? 0 : s.percentage}
                      color={AUDIENCE_RING_COLOR[audience]}
                      size={90}
                      stroke={8}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-base font-bold text-surface-900">
                        {q.isLoading ? '…' : `${s.percentage}%`}
                      </span>
                    </div>
                  </div>

                  <div className="min-w-0">
                    <p className="text-xl font-bold text-surface-900">
                      {q.isLoading ? '—' : s.doneCount}
                      <span className="text-sm font-normal text-surface-400"> / {TOTAL_DAYS}</span>
                    </p>
                    <p className="text-xs text-surface-500 mt-0.5">days complete</p>
                  </div>
                </div>

                {/* Status breakdown */}
                <div className="space-y-1.5 flex-1">
                  {(
                    [
                      { label: 'Published',   value: s.published,   dot: 'bg-indigo-400'  },
                      { label: 'Completed',   value: s.completed,   dot: 'bg-emerald-400' },
                      { label: 'In Progress', value: s.in_progress, dot: 'bg-amber-400'   },
                      { label: 'Not Started', value: s.not_started, dot: 'bg-slate-300'   },
                    ] as const
                  ).map(row => (
                    <div key={row.label} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className={cn('h-1.5 w-1.5 rounded-full', row.dot)} />
                        <span className="text-surface-500">{row.label}</span>
                      </div>
                      <span className="font-medium text-surface-700 tabular-nums">
                        {q.isLoading ? '—' : row.value}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Link */}
                <Link
                  to={`/content/${audience}`}
                  className={cn(
                    'mt-4 flex items-center justify-center gap-1.5 rounded-lg border py-2',
                    'text-xs font-medium transition-colors',
                    AUDIENCE_BG[audience],
                    AUDIENCE_TEXT[audience],
                    'hover:opacity-80',
                  )}
                >
                  View content
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Bottom two-column ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Needs Attention */}
        <section className="rounded-xl border border-surface-200 bg-white shadow-card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-100 flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-500" />
            <h2 className="text-sm font-semibold text-surface-800">Needs Attention</h2>
            {needsAttention.length > 0 && (
              <span className="ml-auto text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                {needsAttention.length}
              </span>
            )}
          </div>

          {isLoadingStats ? (
            <div className="p-5 space-y-2">
              {[1,2,3].map(i => (
                <div key={i} className="h-9 rounded-lg bg-surface-100 animate-pulse" />
              ))}
            </div>
          ) : needsAttention.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-300 mx-auto mb-2" />
              <p className="text-sm text-surface-500">No days in progress — all clear!</p>
            </div>
          ) : (
            <div className="divide-y divide-surface-50">
              {needsAttention.map(({ audience, dayNumber }) => {
                const info = getDayInfo(dayNumber);
                return (
                  <div
                    key={`${audience}-${dayNumber}`}
                    className="flex items-center gap-3 px-5 py-3 hover:bg-surface-50 transition-colors"
                  >
                    <span className={cn('h-2 w-2 rounded-full shrink-0', AUDIENCE_DOT[audience])} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-surface-800 truncate">
                        Day {dayNumber}
                        {info && (
                          <span className="font-normal text-surface-400">
                            {' '}— Wk {info.weekNumber}, Mo {info.monthNumber}
                          </span>
                        )}
                      </p>
                      <p className={cn('text-[11px]', AUDIENCE_TEXT[audience])}>
                        {AUDIENCE_LABELS[audience]}
                      </p>
                    </div>
                    <Link
                      to={`/content/${audience}/day/${dayNumber}`}
                      className="shrink-0 flex items-center gap-1 text-xs font-medium
                                 text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      Edit
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Recent Activity */}
        <section className="rounded-xl border border-surface-200 bg-white shadow-card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-100 flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary-500" />
            <h2 className="text-sm font-semibold text-surface-800">Recent Activity</h2>
          </div>

          {activityLoading ? (
            <div className="p-5 space-y-3">
              {[1,2,3,4].map(i => (
                <div key={i} className="flex gap-3">
                  <div className="h-7 w-7 rounded-full bg-surface-100 animate-pulse shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 bg-surface-100 rounded animate-pulse w-3/4" />
                    <div className="h-3 bg-surface-100 rounded animate-pulse w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : !recentActivity || recentActivity.length === 0 ? (
            <div className="p-8 text-center">
              <Activity className="h-8 w-8 text-surface-200 mx-auto mb-2" />
              <p className="text-sm text-surface-500">No activity yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-surface-50 overflow-y-auto max-h-[380px]">
              {recentActivity.map((log: AuditLog) => (
                <ActivityRow key={log.id} log={log} />
              ))}
            </div>
          )}
        </section>

      </div>
    </div>
  );
}

// ─── Activity row ──────────────────────────────────────────────────────────────

function ActivityRow({ log }: { log: AuditLog }) {
  const label    = AUDIT_ACTION_LABELS[log.action] ?? log.action;
  const isContent = log.action.startsWith('content') || log.action.startsWith('audio') || log.action.startsWith('image');

  return (
    <div className="flex items-start gap-3 px-5 py-3 hover:bg-surface-50 transition-colors">
      {/* Icon bubble */}
      <div className="shrink-0 flex h-7 w-7 items-center justify-center rounded-full bg-surface-100 text-surface-500 mt-0.5">
        <ActionIcon action={log.action} />
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-sm text-surface-800 leading-snug">
          <span className="font-medium">{log.userDisplayName}</span>
          {' '}
          <span className="text-surface-500">{label.toLowerCase()}</span>
          {isContent && log.audience && log.dayNumber != null && (
            <>
              {' '}
              <span className="text-surface-400">
                · {AUDIENCE_LABELS[log.audience as Audience]} Day {log.dayNumber}
              </span>
            </>
          )}
        </p>
        <p className="text-[11px] text-surface-400 mt-0.5">
          {formatRelativeTime(log.timestamp)}
        </p>
      </div>

      {/* Quick link to the day if available */}
      {isContent && log.audience && log.dayNumber != null && (
        <Link
          to={`/content/${log.audience}/day/${log.dayNumber}`}
          className="shrink-0 text-primary-500 hover:text-primary-700 mt-0.5"
        >
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      )}
    </div>
  );
}
